/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { first, sumBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';

import { MeContext } from 'blb-contexts/Me';
import { SubjectContext } from 'blb-contexts/Subject';
import { getPersona, isUnsignedESignNotificationPersona } from 'blb-persona/index';
import { BlendBridge } from 'blb-types/blendBridge';
import {
  getSortedDocsWithTagByMinute,
  groupDocumentsByRedrawWindows,
  groupPreClosingDesignationDocuments,
} from 'blb-utils/closing';
import { getDocumentsDisplayInfo } from 'blb-utils/documents';

import { DocumentActions } from '../DocumentActions';
import { SectionCard } from '../SectionCard';
import { EcloseDocumentTypeLabels } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { isDocDownloadable } from 'blb-components/AuthenticatedApp/DashboardPage/docUtils';

export namespace DocumentsCard {
  export interface Props {
    auditEvents?: EClose.FullClosing['auditEvents'];
    closingDesignation?: EClose.DocumentClosingDesignation;
    documentTypeLabel?: string;
    documents: BlendBridge.API.Document[];
    headerChildren?: React.ReactNode;
    id: string;
    isUploaded?: boolean;
    removeDoc?: (id: string) => void;
    sectionActions?: React.ReactNode;
    title: string;
  }
}

export const DocumentsCard: React.FC<DocumentsCard.Props> = ({
  auditEvents,
  closingDesignation,
  documents,
  headerChildren,
  id,
  isUploaded = false,
  removeDoc,
  title,
  documentTypeLabel,
}) => {
  const [shouldShowUnsignedModal, setShouldShowUnsignedModal] = useState<boolean>(false);
  const [areDocDetailsOpen, setDocDetailsOpen] = useState<boolean>(false);
  const [areDocDetailsViewOnlyOpen, setDocDetailsViewOnlyOpen] = useState<boolean>(false);

  const subject = useContext(SubjectContext);
  const me = useContext(MeContext);
  const context = React.useContext(SubjectContext);
  const persona = getPersona(me!.principalType);

  const docsWithTag = useMemo(() => {
    // for uploaded documents: transform to homogenize types & sort by minute
    if (isUploaded || !auditEvents) {
      return getSortedDocsWithTagByMinute(documents);
    }
    if (closingDesignation === 'PRE_CLOSING') {
      return groupPreClosingDesignationDocuments(auditEvents, documents);
    }
    return groupDocumentsByRedrawWindows(auditEvents, documents);
  }, [auditEvents, documents]);

  const shouldShowViewDocuments = () => {
    return (
      (context?.closing?.closingType === 'HYBRID' || context?.closing?.closingType === 'RON') &&
      me?.principalType === 'settlement_agent' &&
      documentTypeLabel === EcloseDocumentTypeLabels.ESIGN_CLOSING_PACKAGE &&
      docsWithTag.length > 0 &&
      first(docsWithTag)!.actions?.includes('VIEW') &&
      !isDocDownloadable(first(docsWithTag)!)
    );
  };

  const onHeaderClick = () => {
    if (shouldShowViewDocuments()) {
      setDocDetailsViewOnlyOpen(true);
    } else {
      setDocDetailsOpen(true);
    }
  };

  useEffect(() => {
    if (
      isUnsignedESignNotificationPersona(persona) &&
      persona.shouldShowUnsignedESignUIs(subject) &&
      persona.shouldDownloadModalInterrupt(title)
    ) {
      setShouldShowUnsignedModal(true);
    }
  }, [subject]);

  const numberOfNewDocs = sumBy(docsWithTag, ({ sentInMostRecentBatch }) => (sentInMostRecentBatch ? 1 : 0));
  const latestDocWithTag = first(docsWithTag);

  return latestDocWithTag ? (
    <SectionCard
      header={title}
      headerChildren={headerChildren}
      headerTag={numberOfNewDocs ? `${numberOfNewDocs} ADDED` : undefined}
      id={id}
      onHeaderClick={onHeaderClick}
      sectionActions={
        <DocumentActions
          areDocDetailsOpen={areDocDetailsOpen}
          areDocDetailsViewOnlyOpen={areDocDetailsViewOnlyOpen}
          collateFileName={title}
          documentTypeLabel={documentTypeLabel}
          documents={docsWithTag}
          isUploaded={isUploaded}
          removeDoc={removeDoc}
          setDocDetailsOpen={setDocDetailsOpen}
          setDocDetailsViewOnlyOpen={setDocDetailsViewOnlyOpen}
          setShouldShowUnsignedModal={setShouldShowUnsignedModal}
          shouldShowUnsignedModal={shouldShowUnsignedModal}
          title={title}
        />
      }
      subtitle={`${isUploaded ? 'Uploaded' : 'Sent'} on ${getDocumentsDisplayInfo(latestDocWithTag).timeDisplay}`}
    />
  ) : null;
};
